import { useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { BrainCircuit, ArrowRight, CheckCircle2, X, Sparkles, User, Rocket } from "lucide-react";
import { SEO } from "@/components/SEO";
import { useTravelPersonality } from "@/hooks/useTravelPersonality";
import featureQuiz from "@/assets/feature-quiz.jpg";
import featureResults from "@/assets/feature-results.jpg";
import featureShortlist from "@/assets/feature-shortlist.jpg";
import featureItinerary from "@/assets/feature-itinerary.jpg";
import featureBooking from "@/assets/feature-booking.jpg";
import featureWorldmap from "@/assets/feature-worldmap.jpg";

const Landing = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const justSignedUp = (location.state as any)?.justSignedUp === true;
  const signedUpEmail = (location.state as any)?.email as string | undefined;
  const [showWelcome, setShowWelcome] = useState(justSignedUp);
  const { personality } = useTravelPersonality(false);
  const canFastTrack = !!personality && personality.archetype_confidence >= 30 && !!personality.archetype;

  useEffect(() => {
    if (justSignedUp) {
      // Clear navigation state so banner doesn't reappear on back/refresh
      window.history.replaceState({}, '');
    }
  }, [justSignedUp]);

  return (
    <div className="relative overflow-hidden">
      {showWelcome && (
        <div className="bg-highlight text-white">
          <div className="container mx-auto px-4 py-3 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
              <p className="text-sm font-medium truncate">
                Welcome to TripVerse{signedUpEmail ? `, ${signedUpEmail}` : ''}! You're signed in — your saves and trips will now sync to your account.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowWelcome(false)}
              aria-label="Dismiss welcome message"
              className="flex-shrink-0 rounded-full p-1 hover:bg-white/15 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
      {/* Hero Section — Bold navy panel */}
        <SEO title="AI-Powered Travel Destination Finder" description="Take a quick quiz and discover your perfect travel destination with real-time pricing." />
      <section className="relative bg-primary overflow-hidden isolate">
        {/* Decorative geometric shapes */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Large accent circle — top right */}
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-accent/10 blur-3xl" />
          {/* Small accent dot — bottom left */}
          <div className="absolute bottom-20 left-10 w-48 h-48 rounded-full bg-accent/8 blur-2xl" />
          {/* Grid lines */}
          {/* Accent stripe */}
          <div className="absolute top-0 left-0 w-1.5 h-full bg-accent/40" />
        </div>

        <div className="container mx-auto px-4 sm:px-6 py-20 sm:py-32 relative z-10">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/15 text-accent-foreground border border-accent/30 backdrop-blur-sm">
              <BrainCircuit className="h-4 w-4 rotate-90" />
              <span className="text-xs sm:text-sm font-semibold tracking-wide uppercase text-white/90">AI-Powered Travel Decisions</span>
            </div>

            {/* Headline */}
            <h1 className="text-4xl sm:text-6xl md:text-7xl leading-[1.08]" style={{ letterSpacing: '-0.03em' }}>
              <span className="font-normal text-primary-foreground">Let's Decide Your Next</span>
              <br />
              <span className="font-black text-5xl sm:text-7xl md:text-8xl tracking-tight" style={{ color: 'hsl(215 90% 65%)', textShadow: '0 0 40px hsl(215 90% 48% / 0.5)' }}>
                Destination
              </span>
            </h1>

            {/* Subheadline */}
            <p className="text-lg sm:text-xl md:text-2xl text-primary-foreground/70 max-w-2xl mx-auto font-light">
              Skip the overwhelm. Cut the endless scrolling. Tailored options in 2 minutes.
            </p>

            {/* CTA */}
            <div className="pt-4 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                variant="hero"
                size="lg"
                onClick={() => navigate('/quiz')}
                className="text-base sm:text-lg px-10 sm:px-14 py-5 sm:py-7 h-auto group"
              >
                Find Your Next Trip
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              {canFastTrack && (
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => navigate('/quiz?fastTrack=1')}
                  className="text-base sm:text-lg px-8 sm:px-10 py-5 sm:py-7 h-auto group bg-background/10 backdrop-blur-sm text-primary-foreground border-primary-foreground/30 hover:bg-background/20"
                >
                  <Rocket className="mr-2 h-5 w-5" />
                  Continue as {personality!.archetype}
                </Button>
              )}
            </div>

          </div>
        </div>

        {/* Wave separator */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto block">
            <path d="M0 40C240 80 480 0 720 40C960 80 1200 0 1440 40V80H0V40Z" fill="hsl(var(--background) / 0.35)" />
          </svg>
        </div>
      </section>

      {/* Built for the Undecided — Use case framing */}
      <section
        className="py-20 sm:py-28 relative"
        style={{ contentVisibility: "auto", containIntrinsicSize: "1000px" }}
      >
        <div className="absolute inset-0 -z-10 pointer-events-none bg-[radial-gradient(ellipse_at_15%_18%,hsl(var(--accent)/0.28),transparent_55%),radial-gradient(ellipse_at_82%_48%,hsl(var(--highlight)/0.30),transparent_58%),linear-gradient(180deg,transparent,hsl(var(--background)/0.12))]" />
        <div className="absolute top-0 left-0 right-0 h-px bg-border" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-border" />

        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-5xl mx-auto">
            <div className="relative rounded-3xl bg-card border border-border p-8 sm:p-12 md:p-16 overflow-hidden shadow-soft">
              <div className="absolute top-0 left-0 w-1.5 h-full bg-accent" />

              <div className="relative grid md:grid-cols-[auto_1fr] gap-8 md:gap-10 items-start">
                <div className="flex md:block justify-center">
                  <div className="relative">
                    <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-accent/15 border border-accent/30 flex items-center justify-center relative overflow-visible">
                      <User className="h-10 w-10 sm:h-12 sm:w-12 text-accent translate-y-1" strokeWidth={1.75} />
                      <span className="absolute top-1 left-1/2 -translate-x-[210%] text-accent text-base sm:text-lg font-bold leading-none rotate-[-15deg] select-none">?</span>
                      <span className="absolute -top-0.5 left-1/2 -translate-x-1/2 text-accent text-lg sm:text-xl font-bold leading-none select-none">?</span>
                      <span className="absolute top-1 left-1/2 translate-x-[110%] text-accent/70 text-sm sm:text-base font-bold leading-none rotate-[15deg] select-none">?</span>
                    </div>
                  </div>
                </div>

                <div className="text-center md:text-left space-y-5">
                  <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-highlight/10 border border-highlight/30 text-highlight text-xs font-semibold tracking-widest uppercase">
                    <span className="w-1.5 h-1.5 rounded-full bg-highlight animate-pulse" />
                    Built For The Undecided
                  </span>
                  <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight leading-[1.1]">
                    Ready to travel, but{" "}
                    <span className="text-highlight">stuck deciding where?</span>
                  </h2>
                  <p className="text-muted-foreground text-base sm:text-lg font-light leading-relaxed">
                    You're not short on options, you're drowning in them. <span className="text-foreground font-medium">TripVerse exists to make travel decisions simple</span>, for your next trip and every trip after. Answer a few quick questions and <span className="text-foreground font-medium">get a clear shortlist built around exactly what you want</span>, with real destinations, real prices, and real plans.
                  </p>

                  <div className="flex flex-wrap gap-2.5 justify-center md:justify-start pt-3">
                    {["Beat decision paralysis", "No more endless tabs", "No more blog rabbit holes", "No more group chat debates"].map((chip) => (
                      <span key={chip} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-accent/10 border-2 border-accent/40 text-sm sm:text-base font-semibold text-foreground shadow-sm hover:bg-accent/15 hover:border-accent/60 hover:scale-105 transition-all duration-200">
                        <span className="flex items-center justify-center w-5 h-5 rounded-full bg-accent text-accent-foreground text-xs font-black shadow-md">✓</span>
                        {chip}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* More Than a Quiz — Feature Showcase with visuals */}
      <section
        className="py-20 sm:py-28 relative border-t border-border"
        style={{ contentVisibility: "auto", containIntrinsicSize: "1400px" }}
      >
        <div className="absolute inset-0 -z-10 pointer-events-none bg-[radial-gradient(ellipse_at_80%_10%,hsl(var(--accent)/0.20),transparent_55%),radial-gradient(ellipse_at_10%_90%,hsl(var(--highlight)/0.18),transparent_55%)]" />

        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-3xl mx-auto text-center mb-16 sm:mb-20">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-highlight/10 border border-highlight/30 text-highlight text-xs font-semibold tracking-widest uppercase mb-5">
              <Sparkles className="w-3.5 h-3.5" />
              The Full Journey
            </span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-5 leading-[1.1]">
              From a few quick questions to{" "}
              <span className="text-highlight">a fully booked trip</span>
            </h2>
            <p className="text-muted-foreground text-base sm:text-lg font-light">
              Every step of the TripVerse journey — from the quiz that picks your matches to the booking links that lock it in. Create a free account to keep it all in one place.
            </p>
          </div>

          {/* Journey feature blocks */}
          {[
            {
              img: featureQuiz,
              alt: "TripVerse quiz with trip purpose selection",
              tag: "Step 1 · The Quiz",
              title: "A few smart questions, that's it",
              desc: "Tell us where you're flying from, your budget, the vibe you're after. Nine quick steps — no signup, no fluff — and the AI builds a picture of your perfect trip.",
              points: ["Under two minutes to complete", "Climate, purpose, interests and pace", "Your picks visible at every step"],
            },
            {
              img: featureResults,
              alt: "Three destination matches with live prices and a Replace button highlighted",
              tag: "Step 2 · Your Matches",
              title: "Three picks — and you're in control",
              desc: "Each match comes with live flight and hotel prices, a real reason it fits your brief, and the best months to go. Don't love one? Tap Replace and we'll instantly shuffle in a fresh suggestion tailored to your quiz — without losing the ones you've already saved.",
              points: ["Live flight & hotel pricing", "One-tap Replace to shuffle any card", "Save your favourites as you go"],
            },
            {
              img: featureShortlist,
              alt: "Saved destinations dashboard with shortlisted cities",
              tag: "Step 3 · Shortlist",
              title: "Save the ones you love",
              desc: "Heart the picks that feel right, swap out the ones that don't, and come back to a clean shortlist whenever you're ready. No more lost tabs or screenshots buried in your camera roll.",
              points: ["Save unlimited destinations", "Swap suggestions on demand", "Pick up exactly where you left off"],
            },
            {
              img: featureItinerary,
              alt: "Day by day itinerary timeline with activities",
              tag: "Step 4 · Itineraries",
              title: "See your trip, day by day",
              desc: "Generate a tailored itinerary for any saved destination, with daily plans, activities, and budgets that line up with the price you saw on the card.",
              points: ["Personalised daily plans", "Budget-aligned recommendations", "Edit and regenerate any day"],
            },
            {
              img: featureBooking,
              alt: "Booking dialog with curated flight, hotel and activity providers",
              tag: "Step 5 · Book It",
              title: "Book it in one click",
              desc: "When you're ready, we line up the best places to actually book — flights, hotels, tours and car hire — with smart tips for your destination and dates.",
              points: ["Hand-picked booking partners", "Destination-specific tips", "Trip summary auto-filled for you"],
            },
            {
              img: featureWorldmap,
              alt: "World map with visited countries highlighted",
              tag: "Step 6 · Your Map",
              title: "Watch your world map fill up",
              desc: "Log every place you've been and see your personal map light up country by country. A travel record that actually feels rewarding to keep.",
              points: ["Visual world map", "Countries and continents tracked", "A growing record of every trip"],
            },
          ].map((f, i, arr) => (
            <div
              key={f.title}
              className={`grid md:grid-cols-2 gap-10 lg:gap-16 items-center max-w-6xl mx-auto ${i < arr.length - 1 ? "mb-20 sm:mb-28" : ""}`}
            >
              <div className={`order-2 ${i % 2 === 1 ? "md:order-2" : "md:order-1"}`}>
                <div className="relative group">
                  <div className="absolute -inset-3 bg-gradient-to-br from-accent/20 via-highlight/15 to-transparent rounded-3xl blur-xl opacity-60 group-hover:opacity-90 transition-opacity duration-500" />
                  <div className="relative rounded-2xl overflow-hidden border border-border shadow-soft bg-card">
                    <img
                      src={f.img}
                      alt={f.alt}
                      width={1280}
                      height={896}
                      loading="lazy"
                      className="w-full h-auto block"
                    />
                  </div>
                </div>
              </div>
              <div className={`order-1 ${i % 2 === 1 ? "md:order-1" : "md:order-2"} space-y-5`}>
                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent/10 border border-accent/30 text-accent text-xs font-semibold tracking-widest uppercase">
                  {f.tag}
                </span>
                <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight leading-[1.15]">
                  {f.title}
                </h3>
                <p className="text-muted-foreground text-base sm:text-lg font-light leading-relaxed">
                  {f.desc}
                </p>
                <ul className="space-y-2.5 pt-1">
                  {f.points.map((p) => (
                    <li key={p} className="flex items-start gap-3">
                      <span className="flex-shrink-0 mt-1 flex items-center justify-center w-5 h-5 rounded-full bg-highlight text-highlight-foreground text-xs font-black shadow-sm">✓</span>
                      <span className="text-foreground text-sm sm:text-base font-medium">{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}

          {/* Inline CTA */}
          <div className="mt-16 sm:mt-20 max-w-3xl mx-auto text-center">
            <div className="relative rounded-3xl bg-card border border-border p-8 sm:p-10 shadow-soft overflow-hidden">
              <div className="absolute top-0 left-0 w-1.5 h-full bg-highlight" />
              <h3 className="text-2xl sm:text-3xl font-bold tracking-tight mb-3">
                Free to create. Yours forever.
              </h3>
              <p className="text-muted-foreground text-base sm:text-lg font-light mb-6 max-w-xl mx-auto">
                Take the quiz first, no account needed. Sign in any time to keep your saves, trips, and travel map in one place.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Button
                  variant="hero"
                  size="lg"
                  onClick={() => navigate('/quiz')}
                  className="text-base px-8 py-5 h-auto group"
                >
                  Start The Quiz
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => navigate('/auth')}
                  className="text-base px-8 py-5 h-auto"
                >
                  Create Free Account
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>




      {/* Bottom CTA band */}
      <section
        className="relative bg-primary py-16 sm:py-20 overflow-hidden isolate"
        style={{ contentVisibility: "auto", containIntrinsicSize: "420px" }}
      >
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full bg-accent/10 blur-3xl" />
          <div className="absolute -top-20 -right-20 w-60 h-60 rounded-full bg-accent/8 blur-2xl" />
        </div>
        <div className="container mx-auto px-4 sm:px-6 relative z-10 text-center space-y-6">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-primary-foreground tracking-tight">
            Ready to find your next trip?
          </h2>
          <p className="text-primary-foreground/85 text-base sm:text-lg max-w-md mx-auto font-light">
            Skip the overwhelm. Find your next trip in 2 minutes.
          </p>
          <Button
            variant="hero"
            size="lg"
            onClick={() => navigate('/quiz')}
            className="text-base sm:text-lg px-10 sm:px-14 py-5 sm:py-7 h-auto group"
          >
            Get Started
            <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Landing;
